/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShopperBasketsV2 } from '@/scapi';

type ProductItem = ShopperBasketsV2.schemas['ProductItem'];
type Basket = ShopperBasketsV2.schemas['Basket'];

/**
 * Single source of truth for identifying the donation line item. Every
 * cart/order-summary component that needs to know "is this the donation
 * item" or "what's the current donation amount" should go through these
 * helpers rather than re-deriving the check locally.
 */

/** True if `productItem` is the basket's donation line item. */
export function isDonationLineItem(
    productItem: Pick<ProductItem, 'productId'> | undefined,
    donationProductID: string | null | undefined
): boolean {
    return Boolean(donationProductID) && productItem?.productId === donationProductID;
}

/** Filters out the donation line item, e.g. before handing items to `ProductItemsList`. */
export function getVisibleProductItems(
    productItems: ProductItem[] | undefined,
    donationProductID: string | null | undefined
): ProductItem[] {
    if (!donationProductID) {
        return productItems ?? [];
    }
    return (productItems ?? []).filter((item) => !isDonationLineItem(item, donationProductID));
}

/** Returns the basket's donation line item, if one exists. */
export function getDonationLineItem(
    basket: Pick<Basket, 'productItems'> | undefined,
    donationProductID: string | null | undefined
): ProductItem | undefined {
    if (!donationProductID) {
        return undefined;
    }
    return basket?.productItems?.find((item) => isDonationLineItem(item, donationProductID));
}

/** Returns the currently donated amount (0 if no donation is applied). */
export function getDonationAmount(
    basket: Pick<Basket, 'productItems'> | undefined,
    donationProductID: string | null | undefined
): number {
    return getDonationLineItem(basket, donationProductID)?.price ?? 0;
}
